import { useState } from 'react'
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import { useTranslation } from 'react-i18next'
import { useParams } from 'react-router-dom'

import type { PublicOrder } from '@/api/public'
import { publicApi } from '@/api/public'
import { Alert, Button } from '@/components/ui'
import { useApiError } from '@/features/auth/useApiError'
import { QuantityStepper } from '@/features/list/quantity'
import { PartialSubmit } from '@/features/orders/PartialSubmit'
import { STATUS_TONES, formatDate, formatQuantity } from '@/features/orders/status'

/**
 * Η σελίδα που ανοίγει ο προμηθευτής από το email, χωρίς λογαριασμό (§7.6).
 * Καμία πλοήγηση, κανένα κέλυφος εφαρμογής: ένας ξένος βλέπει μόνο αυτό.
 */
export default function PublicOrderPage() {
  const { t } = useTranslation()
  const { token = '' } = useParams()
  const queryClient = useQueryClient()
  const translateError = useApiError()
  const [editing, setEditing] = useState(false)
  const [quantities, setQuantities] = useState<Record<string, string>>({})

  const order = useQuery({
    queryKey: ['public-order', token],
    queryFn: () => publicApi.order(token),
    retry: false,
  })

  const onSettled = (data: PublicOrder) => {
    queryClient.setQueryData(['public-order', token], data)
    setEditing(false)
  }

  const confirm = useMutation({ mutationFn: () => publicApi.confirm(token), onSuccess: onSettled })
  const partial = useMutation({
    mutationFn: (note: string) =>
      publicApi.partial(
        token,
        (order.data?.items ?? []).map((item) => ({
          itemId: item.id,
          confirmedQuantity: quantities[item.id] ?? numeric(item.quantity),
        })),
        note,
      ),
    onSuccess: onSettled,
  })
  const delivered = useMutation({
    mutationFn: () => publicApi.delivered(token),
    onSuccess: onSettled,
  })

  const data = order.data

  return (
    <main className="mx-auto flex min-h-full w-full max-w-2xl flex-col gap-4 px-4 py-6">
      <header className="text-center">
        <p className="text-sm font-semibold uppercase tracking-widest text-brand-700">B2Vend</p>
      </header>

      {order.isPending && <p className="text-center text-gray-500">{t('common.loading')}</p>}
      {order.isError && <Alert>{translateError(order.error)}</Alert>}

      {data && (
        <>
          <section className="rounded-2xl border border-gray-200 bg-white p-5">
            <div className="mb-3 flex items-start justify-between gap-3">
              <div className="min-w-0">
                <h1 className="text-xl font-bold text-gray-900">{t('public.title')}</h1>
                <p className="font-mono text-sm text-gray-500">{data.orderNumber}</p>
              </div>
              <span
                className={`shrink-0 rounded-full border px-3 py-1 text-xs font-medium ${STATUS_TONES[data.status]}`}
              >
                {t(`orders.status.${data.status}`)}
              </span>
            </div>

            <div className="rounded-xl bg-gray-50 p-3 text-sm leading-relaxed">
              <p className="font-semibold text-gray-900">{data.businessName}</p>
              {data.businessVatNumber && (
                <p className="text-gray-600">ΑΦΜ: {data.businessVatNumber}</p>
              )}
              {data.businessPhone && <p className="text-gray-600">{data.businessPhone}</p>}
              {data.businessAddress && <p className="text-gray-600">{data.businessAddress}</p>}
            </div>

            {(data.requestedDeliveryDate || data.requestedDeliverySlot) && (
              <p className="mt-3 rounded-xl border border-brand-200 bg-brand-50 p-3 text-sm text-brand-900">
                <strong>{t('public.requestedDelivery')}:</strong>{' '}
                {[
                  data.requestedDeliveryDate ? formatDate(data.requestedDeliveryDate) : null,
                  data.requestedDeliverySlot
                    ? t(`checkout.slots.${data.requestedDeliverySlot}`)
                    : null,
                  data.requestedDeliveryTime?.slice(0, 5) ?? null,
                ]
                  .filter(Boolean)
                  .join(' · ')}
              </p>
            )}

            {data.orderNote && (
              <p className="mt-3 rounded-xl border border-gray-200 p-3 text-sm text-gray-700">
                {data.orderNote}
              </p>
            )}
          </section>

          <section className="overflow-hidden rounded-2xl border border-gray-200 bg-white">
            <h2 className="border-b border-gray-100 bg-gray-50 px-4 py-3 text-sm font-medium uppercase tracking-wide text-gray-500">
              {t('orders.items')}
            </h2>
            <ul className="divide-y divide-gray-100">
              {data.items.map((item) => (
                <li key={item.id} className="flex items-center justify-between gap-3 px-4 py-3">
                  <div className="min-w-0">
                    <p className="truncate font-medium text-gray-900">{item.name}</p>
                    {item.lineNote && (
                      <p className="truncate text-xs italic text-gray-500">{item.lineNote}</p>
                    )}
                  </div>

                  {editing ? (
                    <div className="flex shrink-0 items-center gap-2">
                      <span className="w-9 text-right text-xs text-gray-400">
                        {t(`units.${item.unit}`)}
                      </span>
                      {/* Ο προμηθευτής απαντά συχνά από κινητό, εν κινήσει:
                          τα ίδια μεγάλα κουμπιά με τη λίστα του πελάτη. */}
                      <QuantityStepper
                        value={quantities[item.id] ?? numeric(item.quantity)}
                        unit={item.unit}
                        compact
                        onChange={(value) =>
                          setQuantities((current) => ({ ...current, [item.id]: value || '0' }))
                        }
                      />
                    </div>
                  ) : (
                    <div className="shrink-0 text-right">
                      <p
                        className={
                          item.confirmedQuantity && item.confirmedQuantity !== item.quantity
                            ? 'text-sm text-gray-400 line-through'
                            : 'font-semibold text-gray-900'
                        }
                      >
                        {formatQuantity(item.quantity)} {t(`units.${item.unit}`)}
                      </p>
                      {item.confirmedQuantity && item.confirmedQuantity !== item.quantity && (
                        <p className="font-semibold text-amber-700">
                          {formatQuantity(item.confirmedQuantity)} {t(`units.${item.unit}`)}
                        </p>
                      )}
                    </div>
                  )}
                </li>
              ))}
            </ul>
          </section>

          {(confirm.isError || partial.isError || delivered.isError) && (
            <Alert>
              {translateError(confirm.error ?? partial.error ?? delivered.error)}
            </Alert>
          )}

          {data.canRespond && !editing && (
            <div className="flex flex-col gap-2">
              <Button loading={confirm.isPending} onClick={() => confirm.mutate()}>
                {t('public.confirmAll')}
              </Button>
              <Button variant="secondary" onClick={() => setEditing(true)}>
                {t('public.declareDifferent')}
              </Button>
            </div>
          )}

          {data.canRespond && editing && (
            <PartialSubmit
              items={data.items}
              quantities={quantities}
              loading={partial.isPending}
              onSubmit={(note) => partial.mutate(note)}
              onCancel={() => setEditing(false)}
            />
          )}

          {!data.canRespond && data.canMarkDelivered && (
            <Button loading={delivered.isPending} onClick={() => delivered.mutate()}>
              {t('public.markDelivered')}
            </Button>
          )}

          {data.status === 'delivered' && (
            <Alert tone="success">{t('public.deliveredThanks')}</Alert>
          )}
          {data.status === 'cancelled' && <Alert>{t('public.cancelled')}</Alert>}

          <footer className="rounded-2xl border border-gray-200 bg-gray-50 p-4 text-center text-sm text-gray-600">
            <p className="mb-3">{t('public.signupPitch')}</p>
            <a
              href="/register"
              className="inline-block rounded-lg border border-brand-700 px-4 py-2 font-semibold text-brand-700"
            >
              {t('public.signupCta')}
            </a>
          </footer>
        </>
      )}
    </main>
  )
}

/** Το API επιστρέφει "5.000"· το πεδίο αριθμού δείχνει "5". */
function numeric(value: string): string {
  const number = Number(value)
  return Number.isFinite(number) ? String(number) : value
}
